/**
 * DOCUMENTATION:
 * Component: CleanCodeNaming
 * Purpose: Educational page teaching the principles of naming variables, functions, and classes.
 * * * Note for Junior Developers:
 * - Names should reveal intent. If a name requires a comment to explain it, the name has failed.
 * - Avoid "disinformation" (e.g., calling a variable 'userList' if it's actually an Object).
 * - Use pronounceable names. If you can't discuss the code in a meeting because the names are
 * gibberish (e.g., `genTimestampYMD`), the names are bad.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
    },
}));

/* ✅ Main Component */
export default function CleanCodeNaming() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const namingRules = useMemo(() => [
        { rule: "Intention-Revealing", example: "daysSinceCreation", description: "The name should tell you why it exists, what it does, and how it is used." },
        { rule: "Searchable Names", example: "MAX_RETRY_ATTEMPTS", description: "Avoid single-letter variables like 'e' or 'j' for complex logic. Use names that are easy to find in a large codebase." },
        { rule: "Pick One Word per Concept", example: "fetchData vs. getData", description: "Don't use 'fetch', 'get', and 'retrieve' for the same action in different classes. Stick to one." },
        { rule: "Boolean Prefixes", example: "isLogged, hasPermission", description: "Use prefixes like 'is', 'has', or 'should' so the variable reads like a true/false question." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. Variables: Intent over Cryptic Shorthand",
            code: `// ❌ JUNIOR: Requires context or comments to understand\nconst d = 34;\nconst list = ['Apple', 'Banana'];\n\n// ✅ SENIOR: The code explains itself\nconst daysUntilExpiration = 34;\nconst fruitStock = ['Apple', 'Banana'];`,
            desc: "Don't worry about the length of the variable name. Modern editors have auto-complete. Accuracy is more valuable than saving five keystrokes."
        },
        {
            title: "2. Functions: Verbs and Keywords",
            code: `// ❌ BAD: 'data' is too vague. What is it doing?\nfunction data() { ... }\n\n// ✅ GOOD: Verbs tell the story\nfunction fetchUserProfile() { ... }\nfunction calculateMonthlyRevenue() { ... }\nfunction validateEmailAddress(email) { ... }`,
            desc: "Functions represent actions. Their names should start with a verb. This makes it clear that calling the function results in an operation."
        },
        {
            title: "3. The Boolean Test",
            code: `// ❌ CONFUSING: What does 'valid' mean? Is it a state or a check?\nif (valid) { ... }\n\n// ✅ CLEAR: Reads like English\nconst isValid = true;\nconst hasUserPaid = false;\nconst shouldRedirect = true;\n\nif (isValid && shouldRedirect) { ... }`,
            desc: "Adding a prefix to booleans creates 'predicate' names. It makes conditional logic read like a logical sentence."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    MEANINGFUL NAMING
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Naming is Documentation
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            In Clean Code, we aim for <strong>Self-Documenting Code</strong>. This means the names you choose should be so clear that anyone reading your code understands the logic without needing a separate README or dozens of comments. Good names act as a roadmap for your teammates.
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>The Naming Cheat Sheet</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Rule</div>
                                    <div>Good Example</div>
                                    <div>Why it works</div>
                                </ResponsiveDataRow>
                            )}
                            {namingRules.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.rule}</Box>
                                    <Box sx={{ fontFamily: "monospace", fontSize: "0.9rem" }}>{item.example}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem" }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#e0b200" },
}));
